import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useReaderCredits } from "../contexts/ReaderCreditContext";
import { useReaderFunnel } from "../contexts/ReaderFunnelContext";
import { ChevronDown, User, CreditCard, Settings, LogOut, Coins, Plus, AlertCircle, HelpCircle } from "lucide-react";
import ReaderCreditsModal from "./ReaderCreditsModal";
import RoleSwitcher from "./RoleSwitcher";

/**
 * ReaderNav - Header for authenticated Reader Mode
 * Used on: /home, /library, /reader/my-library, /player/:id
 * 
 * Clean header with logo, credits display, and user menu dropdown
 */
const ReaderNav = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [creditsModalOpen, setCreditsModalOpen] = useState(false);
  
  // Credits from context
  const { balance, loading, error } = useReaderCredits();
  const { setShowLowCreditModal, setShowCreditExpiredModal, setShowPricingModal } = useReaderFunnel();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Format balance for display
  const formatBalance = () => {
    if (loading) return "...";
    if (balance === null) return "—";
    return balance.toFixed(2);
  };
  
  // Handle credits click - show appropriate modal based on balance
  const handleCreditsClick = () => {
    if (balance !== null && balance === 0) {
      setShowCreditExpiredModal(true);
    } else if (balance !== null && balance < 5) {
      setShowLowCreditModal(true);
    } else {
      setCreditsModalOpen(true);
    }
  };

  return (
    <>
      <nav className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-3 py-2 sm:px-4 sm:py-3 gap-2">
          {/* Logo - Routes to /home for authenticated users */}
          <Link to="/home" className="flex items-center group flex-shrink-0">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-10 sm:h-12 md:h-16 w-auto rounded transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Right Side - Role Switcher + Credits + Buy Button + User Menu */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Role Switcher for multi-role users */}
            <RoleSwitcher className="hidden sm:flex" />
            
            {/* Credits Balance Display - CLICKABLE */}
            <button
              onClick={handleCreditsClick}
              className={`flex items-center gap-1.5 sm:gap-2 rounded-full px-2.5 py-1.5 sm:px-3 sm:py-2 transition-all cursor-pointer hover:ring-2 hover:ring-cyan-400/50 ${
                error ? 'bg-red-900/50 hover:bg-red-900/70' : 
                balance !== null && balance < 5 ? 'bg-yellow-900/50 hover:bg-yellow-900/70' :
                'bg-slate-800/80 hover:bg-slate-700/80'
              }`}
              title={balance !== null && balance < 5 ? "Low credits - click for options" : "Click to view credit transactions"}
            >
              <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center ${
                error ? 'bg-red-400' : 
                balance !== null && balance < 2 ? 'bg-orange-400 animate-pulse' :
                balance !== null && balance < 5 ? 'bg-yellow-400' :
                'bg-amber-400'
              }`}>
                {error ? (
                  <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 text-slate-900" />
                ) : (
                  <Coins className="w-3 h-3 sm:w-4 sm:h-4 text-slate-900" />
                )}
              </div>
              <span className={`font-semibold text-sm sm:text-base ${
                error ? 'text-red-400' : 
                balance !== null && balance < 2 ? 'text-orange-400' :
                balance !== null && balance < 5 ? 'text-yellow-400' :
                'text-amber-400'
              }`}>
                {formatBalance()}
              </span>
            </button>

            {/* Buy Credits Button */}
            <button
              onClick={() => setShowPricingModal(true)}
              className="flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-900 font-semibold rounded-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm transition-all hover:shadow-lg hover:shadow-amber-500/25"
            >
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Buy Credits</span>
              <span className="sm:hidden">Buy</span>
            </button>

            {/* Help Link */}
            <Link
              to="/help/credits/understanding-credits"
              className="p-1.5 text-slate-400 hover:text-amber-400 transition-colors"
              title="Learn about credits"
            >
              <HelpCircle className="w-4 h-4" />
            </Link>

            {/* User Menu Dropdown */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-1.5 sm:gap-2 rounded-full bg-slate-800 px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-slate-100 hover:bg-slate-700 transition-colors"
              >
                <User className="w-4 h-4" />
                <span className="hidden md:inline">{user?.email?.split('@')[0] || 'Account'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {userMenuOpen && (
                <>
                  {/* Backdrop to close menu */}
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setUserMenuOpen(false)}
                  />
                  
                  {/* Menu Panel */}
                  <div className="absolute right-0 mt-2 w-48 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50">
                    <div className="py-2">
                      <Link
                        to="/account/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                      <div className="border-t border-slate-700 my-2" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-slate-800 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Transaction Modal */}
      <ReaderCreditsModal
        isOpen={creditsModalOpen}
        onClose={() => setCreditsModalOpen(false)}
      />
    </>
  );
};

export default ReaderNav;
